"use client"

import { useEffect, useMemo, useReducer, useRef, useState } from "react"
import { ChevronLeft, MessageCircle, RefreshCcw, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { ChatBubble, TypingIndicator } from "./ChatBubble"
import { OptionButtons } from "./OptionButtons"
import { InputField } from "./InputField"
import { LeadForm } from "./LeadForm"
import { BOTON, CONTROL } from "./estilos"
import { FLOWS, START_STEP } from "@/lib/chatbot/flows"
import { buildCharacteristics, buildMessage } from "@/lib/chatbot/buildMessage"
import { buildExtractedTags, buildIntent } from "@/lib/chatbot/buildTags"
import { submitChatbotLead } from "@/lib/chatbot/submitInquiry"
import type {
  Answers,
  ChatbotLeadPayload,
  FlowIntent,
  FlowState,
  Language,
  LeadFormValues,
  Step,
  StepId,
} from "@/lib/chatbot/types"

type TranscriptEntry =
  | { kind: "bot"; stepId: StepId; text: string }
  | { kind: "user"; text: string }

type Action =
  | { type: "selectOption"; saveAs?: string; value: string; userEcho: string; next: StepId }
  | { type: "submitInput"; saveAs: string; value: string; userEcho: string; next: StepId }
  | { type: "submitLead"; values: LeadFormValues; userEcho: string }
  | { type: "submitStart" }
  | { type: "submitSuccess" }
  | { type: "submitError"; error: string }
  | { type: "setLanguage"; language: Language }
  | { type: "back" }
  | { type: "reset" }

type State = FlowState & {
  transcript: TranscriptEntry[]
  transcriptCount: number
}

function initialState(language: Language): State {
  return {
    language,
    answers: {},
    currentStep: START_STEP,
    history: [],
    submitting: false,
    submitted: false,
    submitError: null,
    transcript: [],
    transcriptCount: 0,
  }
}

function pushBot(state: State, stepId: StepId, text: string): TranscriptEntry[] {
  const last = state.transcript[state.transcript.length - 1]
  if (last && last.kind === "bot" && last.stepId === stepId && last.text === text) return state.transcript
  return [...state.transcript, { kind: "bot", stepId, text }]
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "selectOption": {
      const nextAnswers = action.saveAs
        ? { ...state.answers, [action.saveAs]: action.value }
        : state.answers
      return {
        ...state,
        answers: nextAnswers,
        history: [...state.history, state.currentStep],
        currentStep: action.next,
        transcript: [...state.transcript, { kind: "user", text: action.userEcho }],
      }
    }
    case "submitInput": {
      const nextAnswers = {
        ...state.answers,
        [action.saveAs]: action.value,
      }
      return {
        ...state,
        answers: nextAnswers,
        history: [...state.history, state.currentStep],
        currentStep: action.next,
        transcript: [...state.transcript, { kind: "user", text: action.userEcho }],
      }
    }
    case "submitLead": {
      return {
        ...state,
        answers: {
          ...state.answers,
          lead_name: action.values.name,
          lead_email: action.values.email,
          lead_phone: action.values.phone,
          lead_rgpd: action.values.rgpd,
        },
        transcript: [...state.transcript, { kind: "user", text: action.userEcho }],
      }
    }
    case "submitStart":
      return { ...state, submitting: true, submitError: null }
    case "submitSuccess":
      return {
        ...state,
        submitting: false,
        submitted: true,
        history: [...state.history, state.currentStep],
        currentStep: "END",
      }
    case "submitError":
      return { ...state, submitting: false, submitError: action.error }
    case "setLanguage":
      return { ...state, language: action.language }
    case "back": {
      if (state.history.length === 0) return state
      const prev = state.history[state.history.length - 1]
      return {
        ...state,
        currentStep: prev,
        history: state.history.slice(0, -1),
        transcript: state.transcript.slice(0, -1),
        submitError: null,
      }
    }
    case "reset":
      return initialState(state.language)
  }
}

const UI = {
  header: { es: "Person Inmobiliaria", en: "Person Inmobiliaria" },
  open: { es: "Abrir chat", en: "Open chat" },
  close: { es: "Cerrar chat", en: "Close chat" },
  back: { es: "Atrás", en: "Back" },
  reset: { es: "Empezar de nuevo", en: "Start over" },
  errorPrefix: { es: "No se pudo enviar:", en: "Could not send:" },
  retry: { es: "Reintentar", en: "Try again" },
  greetingTeaser: { es: "¿En qué podemos ayudarte?", en: "How can we help you?" },
}

function getCurrentStep(state: State): Step {
  return FLOWS[state.currentStep] ?? FLOWS[START_STEP]
}

function resolveIntent(answers: Answers): FlowIntent {
  const intencion = answers.intencion
  if (intencion === 'comprar' || intencion === 'alquilar' || intencion === 'propietario') {
    return intencion
  }
  const motivo = answers.motivo
  if (motivo === 'agente' || motivo === 'admin' || motivo === 'publicidad') return motivo
  return 'agente'
}

export function ChatbotWidget() {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialState('es'))
  const scrollRef = useRef<HTMLDivElement>(null)

  // Only render the widget once hydrated, so the launcher is interactive the
  // moment it appears (otherwise the button shows pre-hydration and clicks are
  // dropped until React attaches its handlers a few seconds later).
  useEffect(() => {
    setMounted(true)
  }, [])

  const currentStep = getCurrentStep(state)

  const transcriptWithCurrent = useMemo<TranscriptEntry[]>(() => {
    return pushBot(state, currentStep.id, currentStep.text[state.language])
  }, [state, currentStep])

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    })
  }, [transcriptWithCurrent.length, state.submitting, state.submitError, open])

  const canGoBack = state.history.length > 0 && !state.submitting && currentStep.kind !== 'end'

  async function submitLead(values: LeadFormValues, spam: Record<string, string>) {
    if (currentStep.kind !== 'leadForm') return
    const userEcho =
      state.language === 'es'
        ? `${values.name} · ${values.email}${values.phone ? ' · ' + values.phone : ''}`
        : `${values.name} · ${values.email}${values.phone ? ' · ' + values.phone : ''}`
    dispatch({ type: 'submitLead', values, userEcho })
    dispatch({ type: 'submitStart' })

    const flowIntent = resolveIntent(state.answers)
    const isOwner = currentStep.submitType === 'valuation'
    const message = buildMessage(flowIntent, state.answers, state.language)

    const rawRef = typeof state.answers.ref_code === 'string' ? state.answers.ref_code.trim() : ''
    const refCode = rawRef.length > 0 ? rawRef : undefined

    // 'listing' means "one concrete property" in the CRM, so it's only valid
    // when we're also sending its reference. Without one the lead is a search,
    // and it has to file as 'contact' or the CRM can't resolve anything.
    const type =
      currentStep.submitType === 'listing' && !refCode ? 'contact' : currentStep.submitType

    const intent = buildIntent(flowIntent)
    const extractedTags = buildExtractedTags(flowIntent, state.answers)

    const payload: ChatbotLeadPayload = {
      type,
      language: state.language,
      lead: {
        name: values.name,
        email: values.email,
        phone: values.phone || undefined,
        rgpd: true as const,
      },
      message,
      company_url: spam.company_url,
      form_token: spam.form_token,
      captcha_token: spam.captcha_token,
      ...(refCode ? { ref_code: refCode } : {}),
      ...(intent ? { intent } : {}),
      ...(Object.keys(extractedTags).length > 0 ? { extracted_tags: extractedTags } : {}),
      ...(isOwner
        ? {
            valuation: {
              intention: (state.answers.intention === 'alquilar' ? 'alquilar' : 'vender') as
                | 'vender'
                | 'alquilar',
              property_type:
                typeof state.answers.property_type === 'string'
                  ? state.answers.property_type
                  : undefined,
              property_location:
                typeof state.answers.property_location === 'string'
                  ? state.answers.property_location
                  : undefined,
              property_sqmts:
                typeof state.answers.property_sqmts === 'string' && /^\d+$/.test(state.answers.property_sqmts)
                  ? parseInt(state.answers.property_sqmts, 10)
                  : typeof state.answers.property_sqmts === 'number'
                    ? state.answers.property_sqmts
                    : undefined,
              characteristics: buildCharacteristics(state.answers, state.language),
            },
          }
        : {}),
    }

    const res = await submitChatbotLead(payload)
    if (res.ok) {
      dispatch({ type: 'submitSuccess' })
    } else {
      dispatch({ type: 'submitError', error: res.error })
    }
  }

  function renderFooter() {
    if (state.submitting) return <TypingIndicator />
    if (currentStep.kind === 'options') {
      return (
        <OptionButtons
          options={currentStep.options}
          language={state.language}
          disabled={state.submitting}
          onSelect={opt =>
            dispatch({
              type: 'selectOption',
              saveAs: currentStep.saveAs,
              value: opt.value,
              userEcho: opt.label[state.language],
              next: opt.next,
            })
          }
        />
      )
    }
    if (currentStep.kind === 'input') {
      return (
        <InputField
          inputType={currentStep.inputType}
          placeholder={currentStep.placeholder}
          language={state.language}
          disabled={state.submitting}
          onSubmit={value =>
            dispatch({
              type: 'submitInput',
              saveAs: currentStep.saveAs,
              value,
              userEcho: value,
              next: currentStep.next,
            })
          }
        />
      )
    }
    if (currentStep.kind === 'leadForm') {
      return (
        <LeadForm
          fields={currentStep.fields}
          language={state.language}
          disabled={state.submitting}
          onSubmit={submitLead}
        />
      )
    }
    if (currentStep.kind === 'redirect') {
      return (
        <a
          href={currentStep.url}
          target="_blank"
          rel="noopener noreferrer"
          className={BOTON}
        >
          {currentStep.buttonLabel[state.language]}
        </a>
      )
    }
    if (currentStep.kind === 'end') {
      return (
        <button
          type="button"
          onClick={() => dispatch({ type: 'reset' })}
          className={cn(CONTROL, "flex h-9 w-full cursor-pointer items-center justify-center gap-2 transition-colors hover:border-[#193144]")}
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          {UI.reset[state.language]}
        </button>
      )
    }
    return null
  }

  if (!mounted) return null

  return (
    <>
      {/* Launcher — stays anchored at the corner so the panel pops above it.
          Hidden on mobile while open since the panel is near-fullscreen there. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? UI.close[state.language] : UI.open[state.language]}
        className={cn(
          "fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full shadow-lg text-white items-center justify-center cursor-pointer hover:scale-105 transition-transform",
          open ? "hidden sm:flex" : "flex"
        )}
        style={{ background: '#193144' }}
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Panel */}
      {open && (
        <div
          className={cn(
            // Por encima del menú (10001) y del aviso de cookies: en móvil ocupa casi toda la
            // pantalla, y con menos el menú le tapa la cabecera y el botón de cerrar.
            "fixed z-[10050] flex flex-col overflow-hidden border border-gray-200 bg-white shadow-lg",
            // Mobile: near-fullscreen
            "inset-x-2 bottom-2 top-2 rounded-xl",
            // Desktop: panel pops above the launcher button (bottom-24 clears the h-14 launcher at bottom-6)
            "sm:inset-auto sm:bottom-24 sm:right-6 sm:top-auto sm:w-[380px] sm:h-[600px] sm:max-h-[calc(100dvh-8rem)]"
          )}
          role="dialog"
          aria-label={UI.header[state.language]}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-4 py-3">
            {canGoBack && (
              <button
                type="button"
                onClick={() => dispatch({ type: 'back' })}
                aria-label={UI.back[state.language]}
                className={cn(CONTROL, "flex size-8 shrink-0 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-900")}
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            <span className="truncate text-sm font-medium text-gray-900">{UI.header[state.language]}</span>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <div className={cn(CONTROL, "flex h-8 items-center gap-0.5 p-0.5 text-xs")}>
                {(['es', 'en'] as const).map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => dispatch({ type: 'setLanguage', language: lang })}
                    aria-pressed={state.language === lang}
                    className={cn(
                      'h-full cursor-pointer rounded-[4px] px-2 transition-colors',
                      state.language === lang ? 'bg-[#193144] font-medium text-white' : 'text-gray-500 hover:text-gray-900'
                    )}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={UI.close[state.language]}
                className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:text-gray-900"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Thread */}
          <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
            {transcriptWithCurrent.map((entry, idx) =>
              entry.kind === 'bot' ? (
                <ChatBubble key={idx} role="bot">{entry.text}</ChatBubble>
              ) : (
                <ChatBubble key={idx} role="user">{entry.text}</ChatBubble>
              )
            )}
            {state.submitError && (
              <ChatBubble role="bot">
                <span className="text-red-600">
                  {UI.errorPrefix[state.language]} {state.submitError}
                </span>
              </ChatBubble>
            )}
          </div>

          {/* Footer with input/options */}
          <div className="shrink-0 border-t border-gray-200 bg-white p-4">{renderFooter()}</div>
        </div>
      )}
    </>
  )
}
